import { Fragment, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import NavigationTab from 'src/shared/components/ui/navigation/tabs-navigation';
import PageMain from 'src/shared/components/layout/PageMain';
import PageBody from 'src/shared/components/layout/PageBody';
import { ReactChildren } from 'src/shared/types/util.types';
import { Role } from 'src/shared/types/organisation.types';
import { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import PageHeaderBar from 'src/shared/components/layout/PageHeaderBar';
import { FeatureFlag } from 'src/shared/constants/featureFlags.constant';
import { testId } from 'tests/e2e/utils/test-ids.constant';
import { useBreadcrumbLevel } from 'src/shared/hooks/useBreadcrumbLevel';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { FEATURE_FLAGS } from 'src/shared/constants/featureFlags.constant';

interface NavigationItem {
  title: string;
  href: string;
  icon: Icons;
  allowedRoles?: Role[];
  supportOnly: boolean;
  featureFlag?: FeatureFlag;
  testId?: string;
  badge?: string;
}

const navigationItems: NavigationItem[] = [
  {
    title: 'Automation Flows',
    href: '/automations',
    icon: 'Workflow' as Icons,
    allowedRoles: undefined,
    supportOnly: false,
    testId: testId.automations.navigation.flows,
  },
  {
    title: 'Templates',
    href: '/automations/templates',
    icon: 'Files' as Icons,
    allowedRoles: undefined,
    supportOnly: false,
  },
  {
    title: 'Custom Actions',
    href: '/automations/code',
    icon: 'TekstAppIcon' as Icons,
    allowedRoles: ['ADMIN', 'SALES'] as Role[],
    supportOnly: false,
    testId: testId.automations.navigation.customActions,
  },
];

export function AutomationsNav({ children }: ReactChildren) {
  const { pathname } = useLocation();
  const { role, isSupport, featureFlags } = useOrg();
  const useFlowsRoute = featureFlags?.has(FEATURE_FLAGS.AUTOMATIONS_V12) ?? false;

  // Transform navigation items to use /flows routes if feature flag is enabled
  const transformedItems = useMemo(() => {
    if (!useFlowsRoute) return navigationItems;
    return navigationItems.map((item) => ({
      ...item,
      href: item.href.replace('/automations', '/flows'),
    }));
  }, [useFlowsRoute]);

  const selectedItem = useMemo(() => {
    const basePath = useFlowsRoute ? '/flows' : '/automations';

    // Special case for exact /automations or /flows path to match the first item
    if (pathname === basePath) {
      return transformedItems[0];
    }

    if (pathname === `${basePath}/templates`) {
      return transformedItems[1];
    }

    // For code path, return the code navigation item
    if (pathname.includes(`${basePath}/code`)) {
      return transformedItems[2];
    }

    // For other automation/flow paths that aren't templates or code, return the flows item
    if (pathname.startsWith(basePath)) {
      return transformedItems[0];
    }

    // Fallback to the first item
    return transformedItems[0];
  }, [pathname, useFlowsRoute, transformedItems]);

  useBreadcrumbLevel(1, 'Automations', useFlowsRoute ? '/flows' : '/automations');

  // Compute visible items unconditionally to satisfy hooks rules
  const visibleItems = useMemo(() => {
    return transformedItems.filter((item) => {
      // Feature-flagged items are shown only if enabled
      if (item.featureFlag && !featureFlags?.has(FEATURE_FLAGS[item.featureFlag as FeatureFlag])) {
        return false;
      }
      // If roles are specified, only show when user is support or role is allowed
      if (item.allowedRoles && item.allowedRoles.length > 0) {
        return isSupport || item.allowedRoles.includes(role as Role);
      }
      // Support-only items: only show to support
      if (item.supportOnly) {
        return isSupport;
      }
      return true;
    });
  }, [featureFlags, isSupport, role, transformedItems]);

  const basePath = useFlowsRoute ? '/flows' : '/automations';
  const allowedPaths = [`${basePath}`, `${basePath}/code`, `${basePath}/templates`];

  if (!allowedPaths.includes(pathname)) {
    return children;
  }

  return (
    <PageMain excludeMaxWidth>
      <PageHeaderBar noBorder fallbackBreadcrumb={{ label: 'Automations', href: basePath }} />
      <div className="max-w-full px-5 border-b flex" style={{ gridArea: 'header' }}>
        <div className="w-fit translate-y-[1px] flex items-center gap-3">
          {visibleItems.map((item) => {
            const element = (
              <NavigationTab
                key={item.href}
                testId={item.testId}
                icon={item.icon}
                href={item.href}
                text={item.title}
                isActive={item.href === selectedItem?.href}
                badge={item.badge}
              />
            );

            const needsAccessControl = !!(item.supportOnly || (item.allowedRoles && item.allowedRoles.length > 0));
            if (needsAccessControl) {
              return (
                <WithAccessControl key={item.href} allowedRoles={item.allowedRoles as Role[] | undefined}>
                  {element}
                </WithAccessControl>
              );
            }

            return <Fragment key={item.href}>{element}</Fragment>;
          })}
        </div>
      </div>

      <PageBody allowScroll={false}>{children}</PageBody>
    </PageMain>
  );
}
